/**
 * errorHandler.js
 * Normalises API errors from both legacy (status:400) and Laravel validation (422) formats.
 *
 * Usage:
 *   try { ... } catch(err) { const parsed = parseApiError(err); }
 *
 * Returns:
 *   { message: string, fields: Record<string, string[]> | null, statusCode: number }
 */

export function parseApiError(error) {
    const response = error?.response;
    const statusCode = response?.status ?? 0;
    const data = response?.data;

    // Laravel 422 validation
    if (statusCode === 422) {
        return {
            message: data?.message ?? 'Validation failed.',
            fields: data?.errors ?? null,
            statusCode,
        };
    }

    // Legacy 400 with { status, success, message }
    if (statusCode === 400) {
        return {
            message: data?.message ?? 'Bad request.',
            fields: data?.errors ?? null,
            statusCode,
        };
    }

    if (statusCode === 403) {
        return {
            message: data?.message ?? 'You do not have permission to perform this action.',
            fields: null,
            statusCode,
        };
    }

    if (statusCode === 404) {
        return {
            message: data?.message ?? 'The requested resource was not found.',
            fields: null,
            statusCode,
        };
    }

    if (statusCode === 409) {
        return {
            message: data?.message ?? 'A conflict occurred. Please refresh and try again.',
            fields: null,
            statusCode,
        };
    }

    if (statusCode === 429) {
        return {
            message: 'Too many requests. Please wait a moment and try again.',
            fields: null,
            statusCode,
        };
    }

    if (statusCode >= 500) {
        return {
            message: 'A server error occurred. Please try again later.',
            fields: null,
            statusCode,
        };
    }

    // Network / unknown
    return {
        message: error?.message ?? 'An unexpected error occurred.',
        fields: null,
        statusCode,
    };
}

/**
 * Binds a field-error map returned by parseApiError to an antd Form instance.
 * @param {object} form  - antd Form instance from Form.useForm()
 * @param {Record<string, string[]>} fields
 */
export function bindFormErrors(form, fields) {
    if (!fields || !form) return;
    const fieldErrors = Object.entries(fields).map(([name, messages]) => ({
        name,
        errors: messages,
    }));
    form.setFields(fieldErrors);
}
